import React, { useState } from "react";
import { usePendingChats, useApproveChat } from "../../../api/Chats/chats";
import {
  Loader2,
  CheckCircle2,
  Users,
  User,
  Clock,
  Mail,
  Building,
  Shield,
  Search,
  Filter,
  X,
  Eye,
  Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PendingChatRequests() {
  const { data, isLoading } = usePendingChats();
  const approveChat = useApproveChat();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [viewMode, setViewMode] = useState("cards");

  const pendingChats = Array.isArray(data) ? data : [];

  const filteredChats = pendingChats.filter((chat) => {
    if (chat.approved) return false;

    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = chat.users.some(
      (user) =>
        user.username.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.department.toLowerCase().includes(searchLower)
    );
    const matchesDept =
      !filterDept || chat.users.some((user) => user.department === filterDept);
    return matchesSearch && matchesDept;
  });

  const departments = [
    ...new Set(
      pendingChats.flatMap((chat) => chat.users.map((user) => user.department))
    ),
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
        <Loader2 className="animate-spin w-12 h-12 text-blue-600 mb-4" />
        <p className="text-gray-600 text-lg">Loading pending requests...</p>
      </div>
    );
  }

  if (pendingChats.length === 0) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-gray-50 to-emerald-100 rounded-2xl">
        <div className="bg-white p-8 rounded-full mb-6 shadow-lg">
          <CheckCircle2 className="w-16 h-16 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          All caught up!
        </h3>
        <p className="text-gray-600 text-lg">
          No pending chat requests at the moment.
        </p>
      </div>
    );
  }

  const ChatCard = ({ chat, index }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {chat.isGroupChat ? (
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
              ) : (
                <div className="p-2 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900">
                  {chat.chatName ||
                    (chat.isGroupChat
                      ? "Group Chat Request"
                      : "Direct Chat Request")}
                </h3>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <Clock className="w-4 h-4 mr-1" />
                  {new Date(chat.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedChat(chat)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="View Details"
              >
                <Eye className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Users */}
          <div className="space-y-3 mb-5">
            {chat.users.map((user, idx) => (
              <div
                key={idx}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-2">
                  <p className="font-medium text-gray-900 truncate">
                    {user.username}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      {user.email}
                    </span>
                  </div>
                   <span className="flex items-center text-sm text-gray-500">
                      <Building className="w-3 h-3 mr-1" />
                      {user.department}
                    </span>
                </div>
              </div>
            ))}
          </div>

          {/* Action */}
          <button
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => approveChat.mutate(chat._id)}
            disabled={approveChat.isLoading}
          >
            {approveChat.isLoading ? (
              <Loader2 className="animate-spin w-5 h-5" />
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Approve Chat Request</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="p-4 max-h-screen overflow-auto">
      {/* Header */}
      <div className="mb-3">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Pending Chat Requests
        </h1>
        <p className="text-gray-600 text-sm">
          Review and approve chat requests from team members
        </p>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or department..."
                className="w-full pl-10 text-sm pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("cards")}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === "cards"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600"
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === "table"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600"
              }`}
            >
              Table
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>
            Showing {filteredChats.length} of {pendingChats.length} requests
          </span>
          {(searchTerm || filterDept) && (
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterDept("");
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === "cards" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredChats.map((chat, index) => (
            <ChatCard key={chat._id} chat={chat} index={index} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredChats.map((chat, index) => {
                  const [user1, user2] = chat.users;
                  return (
                    <motion.tr
                      key={chat._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex -space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold border-2 border-white">
                              {user1.username.charAt(0).toUpperCase()}
                            </div>
                            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold border-2 border-white">
                              {user2.username.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user1.username} & {user2.username}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user1.department} • {user2.department}
                            </div>
                          </div>
                        </div>
                      </td>
                    
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(chat.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
                          onClick={() => approveChat.mutate(chat._id)}
                          disabled={approveChat.isLoading}
                        >
                          {approveChat.isLoading ? (
                            <Loader2 className="animate-spin w-4 h-4" />
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedChat && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedChat(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-gray-900">
                    Chat Request Details
                  </h2>
                  <button
                    onClick={() => setSelectedChat(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {selectedChat.users.map((user, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-4 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {user.username}
                          </h3>
                          <p className="text-gray-600">{user.role}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">
                            Email:
                          </span>
                          <p className="text-gray-600">{user.email}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            Department:
                          </span>
                          <p className="text-gray-600">{user.department}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      Request Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-blue-700">
                          Chat Type:
                        </span>
                        <span className="ml-2 text-blue-600">
                          {selectedChat.isGroupChat
                            ? "Group Chat"
                            : "Direct Message"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-blue-700">
                          Created:
                        </span>
                        <span className="ml-2 text-blue-600">
                          {new Date(selectedChat.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex space-x-3">
                  <button
                    className="flex-1 flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-60"
                    onClick={() => {
                      approveChat.mutate(selectedChat._id);
                      setSelectedChat(null);
                    }}
                    disabled={approveChat.isLoading}
                  >
                    {approveChat.isLoading ? (
                      <Loader2 className="animate-spin w-5 h-5" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Approve Request</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setSelectedChat(null)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {filteredChats.length === 0 && (searchTerm || filterDept) && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No requests found
          </h3>
          <p className="text-gray-500 mb-4">
            Try adjusting your search or filter criteria.
          </p>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterDept("");
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
